import React, { useMemo } from "react";
import BackgroundImage from "../Assets/IntegrationsPage/bg.png";
import { useAnimateInView } from "./useAnimateInView";
import { normalizeNumbers, useIcon } from "@SeemplicityDev/shared";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../General/useSeverityAsPriority";

interface IntegrationsPageProps {
  data?: {
    categories: {
      name: string;
      total_datasources_count: number;
      sources: {
        name: string;
        severe_count?: {
          severity: number;
          count: number;
        };
        total_count?: number;
      }[];
    }[];
  };
}

const IntegrationsPage: React.FC<IntegrationsPageProps> = ({ data }) => {
  const { ref, animateClassNames } = useAnimateInView();
  const categoriesNumber = data?.categories?.length || 0;
  const { getDataSourceIcon } = useIcon("LIGHT");
  const { getSeverityField } = useSeverityAsPriority();

  const categoriesList = useMemo(() => {
    return (data?.categories ?? []).sort(
      (a, b) => b.sources.length - a.sources.length
    );
  }, [data?.categories]);

  const manyBigOnes = useMemo(() => {
    return (
      categoriesList.filter((category) => category.sources.length >= 2)
        ?.length >= 3
    );
  }, [categoriesList]);

  return (
    <div
      className={`eoy-page integrations-page ${animateClassNames}`}
      ref={ref}
    >
      <img src={BackgroundImage} alt="Background" className="bg-image" />
      <h2 className="page-title">Critical Risk Hotspots by Data Source</h2>
      <h3 className="page-subtitle">
        Top Data Sources Driving the Highest Number of Prioritized Findings
      </h3>
      <div
        className={`categories-container has-${categoriesNumber} ${manyBigOnes ? "many-big-ones" : ""}`}
      >
        {categoriesList.map((category, index) => {
          return (
            <div
              className={`category-container category-container-${index + 1}`}
              key={category.name}
            >
              <span className="category-title">{`${category.name} (${category.total_datasources_count})`}</span>
              <div
                className={`sources-container ${!!category.sources.find((source) => !!source.severe_count) ? "has-severe-count" : ""}`}
                style={{
                  width: `calc(50px * ${category.sources.length})`,
                  height: `calc(50px * ${category.sources.length})`,
                }}
              >
                {category.sources.map((source, sourceIndex) => {
                  return (
                    <div
                      className={`source-container source-container-${sourceIndex + 1} ${!!source.severe_count ? "has-severe-count" : ""}`}
                      key={source.name}
                    >
                      <img
                        src={getDataSourceIcon(source.name, "S") as string}
                        alt={source.name}
                        className="source-icon"
                      />
                      {!!source.severe_count && (
                        <div className="severe-count-container">
                          <span className="name">{source.name}</span>
                          <span className="total-count">
                            {normalizeNumbers(source.total_count!)}
                          </span>
                          <span
                            className="severe-count"
                            style={{
                              color: getSeverityField(
                                source.severe_count.severity,
                                SeverityFieldsEnum.COLOR
                              ),
                            }}
                          >
                            {normalizeNumbers(source.severe_count.count)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationsPage;
